import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import UpdatesPanel from './UpdatesPanel';
import { trackCollegeView, trackReviewSubmit } from '../analytics';
import { ThumbsUp, ThumbsDown, MapPin, MessageSquare } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/* ── Anonymous session token (stored in localStorage, never sent to server as identity) ── */
const getSessionToken = () => {
  const key = 'cr_session_token';
  let token = localStorage.getItem(key);
  if (!token) {
    token = Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(key, token);
  }
  return token;
};

/* ── Category config ── */
const CATEGORIES = [
  { key: 'placements',     label: 'Placements',      icon: '📈' },
  { key: 'faculty',        label: 'Faculty',          icon: '👨‍🏫' },
  { key: 'infrastructure', label: 'Infrastructure',   icon: '🏛️' },
  { key: 'campusLife',     label: 'Campus Life',      icon: '🎉' },
  { key: 'valueForMoney',  label: 'Value for Money',  icon: '💰' },
];

/* ── Toast ── */
const Toast = ({ message, type = 'success', onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div className={`toast toast--${type}`}>
      <span>{type === 'success' ? '✓' : '✕'}</span>
      {message}
    </div>
  );
};

/* ── Star Rating Input ── */
const StarInput = ({ value, onChange, size = 28 }) => {
  const [hovered, setHovered] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {[1, 2, 3, 4, 5].map(n => (
        <span
          key={n}
          onClick={() => onChange(n === value ? 0 : n)}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          role="button"
          aria-label={`Rate ${n} stars`}
          style={{
            fontSize: size,
            cursor: 'pointer',
            color: n <= (hovered || value) ? '#ffb800' : '#d1d5db',
            transition: 'color 0.1s, transform 0.1s',
            transform: hovered === n ? 'scale(1.2)' : 'scale(1)',
            lineHeight: 1,
            userSelect: 'none',
          }}
        >
          ★
        </span>
      ))}
    </div>
  );
};

/* ── Star Display (read-only) ── */
const StarDisplay = ({ rating, size = 16, showEmpty = true }) => (
  <span style={{ display: 'inline-flex', gap: 2 }}>
    {[1, 2, 3, 4, 5].map(n => (
      <span key={n} style={{
        fontSize: size,
        color: n <= rating ? '#ffb800' : (showEmpty ? '#e5e7eb' : 'transparent'),
        lineHeight: 1,
      }}>★</span>
    ))}
  </span>
);

/* ── Category Rating Bar (read-only) ── */
const CategoryBar = ({ label, icon, value }) => {
  if (!value || value === 0) return null;
  const pct = (value / 5) * 100;
  const color = value >= 4 ? '#059669' : value >= 3 ? '#d97706' : '#dc2626';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 14, width: 20, flexShrink: 0 }}>{icon}</span>
      <span style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
        color: '#666',
        width: 110,
        flexShrink: 0,
        letterSpacing: '0.02em',
      }}>{label}</span>
      <div style={{
        flex: 1, height: 6, background: '#efefef', borderRadius: 9999, overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: color, borderRadius: 9999,
          transition: 'width 0.6s ease',
        }} />
      </div>
      <span style={{
        fontFamily: 'var(--font-primary)',
        fontSize: 12, fontWeight: 700,
        color, width: 24, textAlign: 'right', flexShrink: 0,
      }}>{value.toFixed(1)}</span>
    </div>
  );
};

/* ── Skeleton ── */
const CollegeSkeleton = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
    <div className="skeleton" style={{ height: 48, width: '70%', borderRadius: 8 }} />
    <div style={{ display: 'flex', gap: 10 }}>
      <div className="skeleton" style={{ height: 28, width: 90, borderRadius: 9999 }} />
      <div className="skeleton" style={{ height: 28, width: 120, borderRadius: 9999 }} />
    </div>
  </div>
);

const ReviewSkeleton = ({ delay = 0 }) => (
  <div style={{
    background: '#fff', borderRadius: 24, padding: 28,
    border: '1px solid #efefef',
    display: 'flex', flexDirection: 'column', gap: 12,
    animationDelay: `${delay}s`,
  }}>
    <div style={{ display: 'flex', gap: 6 }}>
      {[1,2,3,4,5].map(n => <div key={n} className="skeleton" style={{ width: 18, height: 18, borderRadius: 4 }} />)}
    </div>
    <div className="skeleton" style={{ height: 14, width: '100%', borderRadius: 4 }} />
    <div className="skeleton" style={{ height: 14, width: '80%', borderRadius: 4 }} />
    <div className="skeleton" style={{ height: 12, width: '30%', borderRadius: 4 }} />
  </div>
);

/* ── Average category ratings across all reviews ── */
const CollegeStats = ({ reviews, college }) => {
  const stats = useMemo(() => {
    const rated = reviews.filter(r => r.categoryRatings);
    if (!rated.length) return null;
    const avg = {};
    CATEGORIES.forEach(({ key }) => {
      const vals = rated.map(r => r.categoryRatings?.[key] || 0).filter(v => v > 0);
      avg[key] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    });
    return avg;
  }, [reviews]);

  const overallAvg = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  return (
    <div style={{
      background: '#fff', borderRadius: 32, padding: '36px 40px',
      marginBottom: 24, border: '1px solid #efefef',
    }}>
      {/* College name & badges */}
      <h1 style={{
        fontFamily: 'var(--font-primary)', fontWeight: 800,
        fontSize: 'clamp(26px, 4vw, 40px)',
        letterSpacing: '-0.03em', color: '#000',
        lineHeight: 1.15, marginBottom: 20,
      }}>
        {college.name}
      </h1>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
        {college.city && (
          <span className="pill-badge pill-badge--outline" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <MapPin size={11} strokeWidth={2} />
            {college.city}{college.state ? `, ${college.state}` : ''}
          </span>
        )}
        {college.nirfRank && (
          <span className="pill-badge pill-badge--blue">NIRF #{college.nirfRank}</span>
        )}
        {college.rank && (
          <span className="pill-badge pill-badge--outline">Rank: {college.rank}</span>
        )}
      </div>

      {/* Overall rating + share */}
      {overallAvg && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: stats ? 28 : 0 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            background: '#fffc52', borderRadius: 9999, padding: '8px 20px',
          }}>
            <StarDisplay rating={Math.round(overallAvg)} size={18} />
            <span style={{ fontFamily: 'var(--font-primary)', fontWeight: 700, fontSize: 18 }}>
              {overallAvg}
            </span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'rgba(0,0,0,0.55)', letterSpacing: '0.04em' }}>
              / 5 · {reviews.length} review{reviews.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* WhatsApp Share */}
          <button
            id="whatsapp-share-btn"
            onClick={() => {
              const url = encodeURIComponent(window.location.href);
              const text = encodeURIComponent(`Check out ${college.name} reviews on CampusReveal`);
              window.open(`https://wa.me/?text=${text}%20${url}`, '_blank');
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: '#25D366', color: '#fff',
              border: 'none', borderRadius: 9999,
              padding: '9px 18px', cursor: 'pointer',
              fontFamily: 'var(--font-primary)', fontSize: 13, fontWeight: 600,
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Share on WhatsApp
          </button>
        </div>
      )}

      {/* Category averages */}
      {stats && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{
            fontFamily: 'var(--font-mono)', fontSize: 11,
            color: '#999', letterSpacing: '0.08em',
            textTransform: 'uppercase', marginBottom: 4,
          }}>Average Ratings</p>
          {CATEGORIES.map(cat => (
            <CategoryBar key={cat.key} icon={cat.icon} label={cat.label} value={stats[cat.key]} />
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Helpful Vote Button ── */
const HelpfulButtons = ({ review, sessionToken, collegeId }) => {
  const myVote = review.helpfulVotes?.includes(sessionToken) ? 'helpful'
    : review.notHelpfulVotes?.includes(sessionToken) ? 'notHelpful' : null;

  const [localHelpful, setLocalHelpful]       = useState(review.helpfulVotes?.length || 0);
  const [localNotHelpful, setLocalNotHelpful] = useState(review.notHelpfulVotes?.length || 0);
  const [voted, setVoted]                     = useState(myVote);
  const [loading, setLoading]                 = useState(false);

  const handleVote = async (vote) => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/colleges/${collegeId}/reviews/${review._id}/vote`,
        { sessionToken, vote }
      );
      setLocalHelpful(res.data.helpfulCount);
      setLocalNotHelpful(res.data.notHelpfulCount);
      setVoted(prev => prev === vote ? null : vote);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  const btnStyle = (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 5,
    background: active ? '#000' : 'transparent',
    color: active ? '#fff' : '#999',
    border: `1px solid ${active ? '#000' : '#e5e7eb'}`,
    borderRadius: 9999,
    padding: '4px 12px',
    fontSize: 12,
    fontFamily: 'var(--font-mono)',
    cursor: loading ? 'wait' : 'pointer',
    transition: 'all 0.15s',
    letterSpacing: '0.02em',
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#bcc1c7', letterSpacing: '0.03em' }}>
        Helpful?
      </span>
      <button style={btnStyle(voted === 'helpful')} onClick={() => handleVote('helpful')}>
        <ThumbsUp size={12} strokeWidth={2} /> {localHelpful > 0 ? localHelpful : ''}
      </button>
      <button style={btnStyle(voted === 'notHelpful')} onClick={() => handleVote('notHelpful')}>
        <ThumbsDown size={12} strokeWidth={2} /> {localNotHelpful > 0 ? localNotHelpful : ''}
      </button>
    </div>
  );
};

/* ── Single Review Card ── */
const ReviewCard = ({ review, index, sessionToken, collegeId }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const hasCategories = review.categoryRatings &&
    Object.values(review.categoryRatings).some(v => v > 0);

  return (
    <div
      className="animate-fade-up"
      style={{
        background: review._optimistic ? '#f8f8ff' : '#fff',
        borderRadius: 24, padding: 28,
        border: review._optimistic ? '1px solid rgba(0,0,255,0.2)' : '1px solid #efefef',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        animationDelay: `${Math.min(index * 0.04, 0.3)}s`,
        transition: 'opacity 0.3s',
        opacity: review._optimistic ? 0.8 : 1,
      }}
    >
      {/* Stars + date row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <StarDisplay rating={review.rating} size={18} />
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#bcc1c7', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 8 }}>
          {review._optimistic && (
            <span style={{ background: '#0000ff', color: '#fff', borderRadius: 9999, padding: '2px 8px', fontSize: 10, letterSpacing: '0.08em' }}>
              Posting…
            </span>
          )}
          {!review._optimistic && formatDate(review.createdAt)}
        </span>
      </div>

      {/* Category bars (compact) */}
      {hasCategories && !review._optimistic && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
          {CATEGORIES.map(cat => (
            review.categoryRatings?.[cat.key] > 0 && (
              <CategoryBar key={cat.key} icon={cat.icon} label={cat.label} value={review.categoryRatings[cat.key]} />
            )
          ))}
        </div>
      )}

      {/* Review text */}
      <p style={{ fontFamily: 'var(--font-primary)', fontSize: 15, lineHeight: 1.7, color: '#222', margin: 0 }}>
        {review.review}
      </p>

      {/* Helpful voting */}
      {!review._optimistic && (
        <HelpfulButtons review={review} sessionToken={sessionToken} collegeId={collegeId} />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════
   MAIN REVIEW PAGE
═══════════════════════════════════════ */
const Review = () => {
  const { collegeId } = useParams();
  const navigate      = useNavigate();
  const sessionToken  = getSessionToken();

  const [college,         setCollege]         = useState(null);
  const [reviews,         setReviews]         = useState([]);
  const [loadingCollege,  setLoadingCollege]  = useState(true);
  const [loadingReviews,  setLoadingReviews]  = useState(true);
  const [error,           setError]           = useState(null);

  // Form state
  const [newReview,    setNewReview]    = useState('');
  const [rating,       setRating]       = useState(0);
  const [catRatings,   setCatRatings]   = useState({ placements: 0, faculty: 0, infrastructure: 0, campusLife: 0, valueForMoney: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter & sort state
  const [sortBy,     setSortBy]     = useState('newest');   // newest | helpful | highest | lowest
  const [filterStar, setFilterStar] = useState(0);         // 0 = all

  const [toast, setToast] = useState(null);
  const showToast = useCallback((message, type = 'success') => setToast({ message, type }), []);

  useEffect(() => {
    axios.get(`${API}/colleges/${collegeId}`)
      .then(r => { setCollege(r.data); trackCollegeView(r.data.name, collegeId); })
      .catch(() => setError('Could not load college data.'))
      .finally(() => setLoadingCollege(false));

    axios.get(`${API}/colleges/${collegeId}/reviews`)
      .then(r => setReviews(r.data))
      .catch(() => {})
      .finally(() => setLoadingReviews(false));
  }, [collegeId]);

  const handleSubmit = async () => {
    if (rating === 0) { showToast('Please select an overall star rating.', 'info'); return; }
    if (!newReview.trim()) { showToast('Please write your review.', 'info'); return; }

    const optimistic = {
      _id: `temp-${Date.now()}`,
      review: newReview, rating, categoryRatings: catRatings,
      createdAt: new Date().toISOString(), _optimistic: true,
    };
    setReviews(prev => [optimistic, ...prev]);
    const [savedReview, savedRating, savedCat] = [newReview, rating, catRatings];
    setNewReview(''); setRating(0);
    setCatRatings({ placements: 0, faculty: 0, infrastructure: 0, campusLife: 0, valueForMoney: 0 });

    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/colleges/${collegeId}/reviews`, {
        review: savedReview, rating: savedRating, categoryRatings: savedCat,
      });
      setReviews(prev => prev.map(r => r._id === optimistic._id ? res.data : r));
      trackReviewSubmit(college?.name, savedRating);
      showToast('Review posted successfully!', 'success');
    } catch {
      setReviews(prev => prev.filter(r => r._id !== optimistic._id));
      setNewReview(savedReview); setRating(savedRating); setCatRatings(savedCat);
      showToast('Failed to post review. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  /* Filtered + sorted reviews */
  const displayedReviews = useMemo(() => {
    let list = [...reviews];
    if (filterStar > 0) list = list.filter(r => r.rating === filterStar);
    switch (sortBy) {
      case 'helpful':  list.sort((a, b) => (b.helpfulVotes?.length || 0) - (a.helpfulVotes?.length || 0)); break;
      case 'highest':  list.sort((a, b) => b.rating - a.rating); break;
      case 'lowest':   list.sort((a, b) => a.rating - b.rating); break;
      default:         list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
    return list;
  }, [reviews, sortBy, filterStar]);

  const chipStyle = (active) => ({
    padding: '6px 14px', borderRadius: 9999, fontSize: 12,
    fontFamily: 'var(--font-mono)', cursor: 'pointer', border: 'none',
    background: active ? '#000' : '#f4f4f4',
    color: active ? '#fff' : '#555',
    letterSpacing: '0.03em', transition: 'all 0.15s',
  });

  return (
    <div style={{ background: '#f4f4f4', minHeight: '100vh' }}>

      {/* ── NAVBAR ── */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid #efefef', padding: '0 40px', height: 64,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <span style={{ fontFamily: 'var(--font-primary)', fontWeight: 800, fontSize: 18, letterSpacing: '-0.02em' }}>
            Campus<span style={{ color: '#0000ff' }}>Reveal</span>
          </span>
        </button>
        <span style={{ color: '#cfcfcf', fontSize: 18 }}>›</span>
        <button onClick={() => navigate('/home')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'var(--font-mono)', fontSize: 13, color: '#999', letterSpacing: '0.02em' }}>
          All Colleges
        </button>
        {college && (
          <>
            <span style={{ color: '#cfcfcf', fontSize: 18 }}>›</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: '#000', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {college.name}
            </span>
          </>
        )}
        <div style={{ marginLeft: 'auto' }}>
          <UpdatesPanel />
        </div>
      </nav>

      {/* ── MAIN ── */}
      <main style={{ maxWidth: 800, margin: '0 auto', padding: '40px 40px 80px' }}>
        {error && (
          <div style={{ background: '#fff0f0', border: '1px solid #ffcccc', borderRadius: 16, padding: '20px 24px', color: '#cc0000', fontFamily: 'var(--font-primary)', fontSize: 15, marginBottom: 32 }}>
            {error}
          </div>
        )}

        {/* College header / stats */}
        {loadingCollege ? (
          <div style={{ background: '#fff', borderRadius: 32, padding: '36px 40px', marginBottom: 24, border: '1px solid #efefef' }}>
            <CollegeSkeleton />
          </div>
        ) : college ? (
          <CollegeStats college={college} reviews={reviews} />
        ) : null}

        {/* ── WRITE REVIEW ── */}
        <div style={{ background: '#121212', borderRadius: 32, padding: '36px 40px', marginBottom: 24 }}>
          <h2 style={{ fontFamily: 'var(--font-primary)', fontWeight: 700, fontSize: 20, color: '#fff', marginBottom: 6 }}>
            Write a Review
          </h2>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: '#666', letterSpacing: '0.04em', marginBottom: 28, textTransform: 'uppercase' }}>
            Anonymous · Genuine · Helpful
          </p>

          {/* Overall rating */}
          <div style={{ marginBottom: 24 }}>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#555', marginBottom: 10 }}>
              Overall Rating *
            </p>
            <StarInput value={rating} onChange={setRating} size={32} />
          </div>

          {/* Category ratings */}
          <div style={{ marginBottom: 24 }}>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#555', marginBottom: 14 }}>
              Category Ratings (optional)
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {CATEGORIES.map(({ key, label, icon }) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 16, width: 22, flexShrink: 0 }}>{icon}</span>
                  <span style={{ fontFamily: 'var(--font-primary)', fontSize: 13, color: '#aaa', width: 120, flexShrink: 0 }}>{label}</span>
                  <StarInput value={catRatings[key]} onChange={v => setCatRatings(p => ({ ...p, [key]: v }))} size={20} />
                  {catRatings[key] > 0 && (
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#666' }}>
                      {catRatings[key]}/5
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Review text */}
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#555', marginBottom: 10 }}>
              Your Review *
            </p>
            <textarea
              className="form-textarea"
              id="review-textarea"
              rows={5}
              value={newReview}
              onChange={e => setNewReview(e.target.value)}
              placeholder="Share your experience — facilities, campus life, placements, professors…"
            />
          </div>

          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#444', marginBottom: 20, lineHeight: 1.6, letterSpacing: '0.02em' }}>
            ↳ Keep it genuine. Avoid offensive language.
          </p>

          <button
            id="submit-review-btn"
            onClick={handleSubmit}
            className="btn-primary"
            disabled={isSubmitting}
            style={{ width: '100%', justifyContent: 'center', fontSize: 16, opacity: isSubmitting ? 0.7 : 1, background: isSubmitting ? '#3333ff' : '#0000ff' }}
          >
            {isSubmitting ? 'Posting…' : 'Post Review →'}
          </button>
        </div>

        {/* ── REVIEWS LIST ── */}
        <div>
          {/* Header + filters */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <h2 style={{ fontFamily: 'var(--font-primary)', fontWeight: 700, fontSize: 20, color: '#000' }}>Reviews</h2>
              {!loadingReviews && (
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: '#999', letterSpacing: '0.04em' }}>
                  {displayedReviews.length} of {reviews.length}
                </span>
              )}
            </div>

            {/* Sort chips */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[['newest','Newest'], ['helpful','Most Helpful'], ['highest','Highest ⭐'], ['lowest','Lowest ⭐']].map(([val, label]) => (
                <button key={val} style={chipStyle(sortBy === val)} onClick={() => setSortBy(val)}>{label}</button>
              ))}
            </div>
          </div>

          {/* Star filter chips */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#999', alignSelf: 'center', letterSpacing: '0.04em' }}>Filter:</span>
            {[0, 5, 4, 3, 2, 1].map(star => (
              <button key={star} style={chipStyle(filterStar === star)} onClick={() => setFilterStar(star)}>
                {star === 0 ? 'All' : `${'★'.repeat(star)} ${star}`}
              </button>
            ))}
          </div>

          {/* Skeletons */}
          {loadingReviews && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[0, 0.1, 0.2].map((d, i) => <ReviewSkeleton key={i} delay={d} />)}
            </div>
          )}

          {/* Empty */}
          {!loadingReviews && displayedReviews.length === 0 && (
            <div style={{ background: '#fff', borderRadius: 32, padding: '60px 40px', textAlign: 'center', border: '1px solid #efefef' }}>
              <MessageSquare size={40} strokeWidth={1.5} style={{ color: '#d1d5db', margin: '0 auto 16px' }} />
              <h3 style={{ fontFamily: 'var(--font-primary)', fontWeight: 700, fontSize: 18, marginBottom: 8 }}>
                {filterStar > 0 ? `No ${filterStar}★ reviews yet` : 'No reviews yet'}
              </h3>
              <p style={{ color: '#999', fontSize: 15 }}>
                {filterStar > 0 ? 'Try a different filter.' : 'Be the first to share your experience.'}
              </p>
            </div>
          )}

          {/* Review cards */}
          {!loadingReviews && displayedReviews.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {displayedReviews.map((review, i) => (
                <ReviewCard
                  key={review._id || i}
                  review={review}
                  index={i}
                  sessionToken={sessionToken}
                  collegeId={collegeId}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
};

export default Review;
